// Persisted-state keys owned by OrganizationContext. Centralized so
// auth-context can clear/snapshot them on signOut and impersonation
// transitions without duplicating magic strings (and without importing
// the OrganizationContext, which lives below AuthProvider in the tree).
export const ORG_STORAGE_KEYS = [
  "etx_active_group_id",
  "etx_active_dealership_id",
  "etx_active_bodyshop_id",
  "etx_active_team_kind",
  "etx_group_view",
] as const

const SNAPSHOT_KEY = "etx_pre_impersonation_org"

export function clearActiveOrg(): void {
  for (const key of ORG_STORAGE_KEYS) {
    localStorage.removeItem(key)
  }
}

// Save the admin's active-org selection before impersonating, so endImpersonation
// can put them back where they were instead of dropping them on a fresh default.
// No-op if a snapshot already exists (nested impersonation must not clobber the
// outermost admin's state).
export function snapshotActiveOrg(): void {
  if (sessionStorage.getItem(SNAPSHOT_KEY) !== null) return
  const snap: Record<string, string> = {}
  for (const key of ORG_STORAGE_KEYS) {
    const val = localStorage.getItem(key)
    if (val !== null) snap[key] = val
  }
  sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snap))
}

// Replace whatever the impersonated user wrote to localStorage with the admin's
// pre-impersonation snapshot. Always clears the org keys first so a partial
// snapshot (e.g. admin had no bodyshop selected) doesn't leak the impersonated
// user's bodyshop id forward.
export function restoreActiveOrg(): void {
  const raw = sessionStorage.getItem(SNAPSHOT_KEY)
  sessionStorage.removeItem(SNAPSHOT_KEY)
  clearActiveOrg()
  if (!raw) return
  try {
    const snap = JSON.parse(raw) as Record<string, string>
    const allowed = new Set<string>(ORG_STORAGE_KEYS)
    for (const [key, val] of Object.entries(snap)) {
      if (allowed.has(key) && typeof val === "string") {
        localStorage.setItem(key, val)
      }
    }
  } catch {
    // Corrupt snapshot — leave keys cleared rather than throwing.
  }
}

export function clearOrgSnapshot(): void {
  sessionStorage.removeItem(SNAPSHOT_KEY)
}
